/**
 * Iter 427.p40 — Portal permission context.
 *
 * Dirk 2026-09-14 (POPIA): "If a maintenance user can't see accounts,
 * the accounts tab must be REMOVED from the screen, not just show zero
 * rows. Every gating — nav, route, docs list, email fan-out — must
 * consult the same source of truth."
 *
 * The source of truth is `GET /portal/me/perms`, which returns the
 * server-resolved `{ portal.jobs.read: bool, ... }` dict for the
 * currently-signed-in portal user (owner preset defaults everything
 * to true; other presets map to the canonical dict in
 * lib/portal_perms.py::PORTAL_PRESETS).
 *
 * Every portal surface reads perms via `usePortalPerms()`:
 *   - PortalLayout NAV filtering (hide inaccessible tabs)
 *   - PortalRouteGuard (redirect if the URL is pasted directly)
 *   - CustomerProfileDocumentsBlock (filter document categories)
 *
 * Non-client users (staff previewing the portal at /portal/preview)
 * get an all-true dict so staff previews stay functional.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const PortalPermsContext = createContext({ perms: null, loading: true, portalPreset: null });

export function PortalPermsProvider({ children }) {
  const { user } = useAuth();
  const [perms, setPerms] = useState(null);
  const [portalPreset, setPortalPreset] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // Staff users previewing the portal bypass the perm check.
    if (user && user.role !== "client") {
      if (alive) {
        setPerms({ __all: true });
        setPortalPreset("owner");
        setLoading(false);
      }
      return () => { alive = false; };
    }
    (async () => {
      try {
        const { data } = await api.get("/portal/me/perms");
        if (!alive) return;
        setPerms(data?.permissions || {});
        setPortalPreset(data?.portal_preset || "owner");
      } catch (_e) {
        // Fail-CLOSED: if the endpoint errors, we assume no perms
        // rather than accidentally opening every tab.
        if (!alive) return;
        setPerms({});
        setPortalPreset(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.id, user?.role]);

  const value = useMemo(() => ({
    perms: perms?.__all ? new Proxy({}, { get: () => true }) : perms,
    portalPreset,
    loading,
    /** True when the current user holds `perm`; also true for staff
     *  previews. Safe to call before perms load — returns false. */
    can(perm) {
      if (!perm) return true;
      if (loading) return false;
      if (perms?.__all) return true;
      return Boolean(perms?.[perm]);
    },
  }), [perms, portalPreset, loading]);

  return (
    <PortalPermsContext.Provider value={value}>
      {children}
    </PortalPermsContext.Provider>
  );
}

export function usePortalPerms() {
  return useContext(PortalPermsContext);
}

/** Convenience hook for a single perm check. */
export function usePortalCan(perm) {
  const { can } = usePortalPerms();
  return can(perm);
}
